import { useState, useEffect } from "react";
import { Close, Visibility, VisibilityOff, CheckCircle } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
  LinearProgress,
  Fade,
  Tooltip,
} from "@mui/material";
import WhiteButtonComponent from "../buttons/white-button-component";
import BlackButtonComponent from "../buttons/black-button-component";
import { useFormik } from "formik";
import * as Yup from "yup";
import { updatePassword } from "services/authService";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { toast } from "react-toastify";

// Constants
const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  pattern: /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]+$/,
};

interface PasswordModalProps {
  open: boolean;
  onClose: () => void;
}

interface PasswordFormValues {
  newPassword: string;
  confirmPassword: string;
}

type UserType = "Merchant" | "Host";

const ChangePasswordModal: React.FC<PasswordModalProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [passwordStrength, setPasswordStrength] = useState<number>(0);
  const userDetails = useSelector(
    (state: RootState) => state.appLayout.userDetails
  ) as any;
  const userId = userDetails?.userId ?? "";

  // Formik Configuration
  const formik = useFormik<PasswordFormValues>({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .required("Password is required")
        .min(
          PASSWORD_REQUIREMENTS.minLength,
          `Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters`
        )
        .matches(
          PASSWORD_REQUIREMENTS.pattern,
          "Must contain an uppercase letter, number, and special character"
        ),
      confirmPassword: Yup.string()
        .required("Confirm your password")
        .oneOf([Yup.ref("newPassword")], "Passwords must match"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const userType = getApiUserType(userDetails?.userType);
        await updatePassword(userType, userId, {
          password: values.newPassword,
        });
        handleSuccess();
      } catch (error) {
        handleError(error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Password strength calculation
  useEffect(() => {
    const calculatePasswordStrength = (password: string): number => {
      if (!password) return 0;
      
      let strength = 0;
      
      // Length check
      if (password.length >= PASSWORD_REQUIREMENTS.minLength) strength += 25;
      
      // Character type checks
      if (/[A-Z]/.test(password)) strength += 25;
      if (/[0-9]/.test(password)) strength += 25;
      if (/[@$!%*?&#]/.test(password)) strength += 25;
      
      return strength;
    };
    
    setPasswordStrength(calculatePasswordStrength(formik.values.newPassword));
  }, [formik.values.newPassword]);

  // Derived Values
  const getApiUserType = (type: string): "Merchants" | "Hosts" =>
    type === "Merchant" ? "Merchants" : "Hosts";

  const handleSuccess = () => {
    toast.success("Password updated successfully");
    formik.resetForm();
    onClose();
  };

  const handleError = (error: unknown) => {
    console.error("Password Update Error:", error);
    toast.error(
      (error as Error)?.message ||
        "Failed to update password. Please try again."
    );
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 50) return theme.palette.error.main;
    if (passwordStrength < 100) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const getPasswordStrengthLabel = () => {
    if (passwordStrength < 50) return "Weak";
    if (passwordStrength < 100) return "Moderate";
    return "Strong";
  };

  // Check requirements
  const hasMinLength = formik.values.newPassword.length >= PASSWORD_REQUIREMENTS.minLength;
  const hasUppercase = /[A-Z]/.test(formik.values.newPassword);
  const hasNumber = /[0-9]/.test(formik.values.newPassword);
  const hasSpecial = /[@$!%*?&#]/.test(formik.values.newPassword);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' || !formik.dirty) {
          onClose();
        }
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{ 
        sx: { 
          borderRadius: 2,
          margin: isMobile ? 2 : 4,
          maxWidth: isMobile ? 'calc(100% - 32px)' : '450px',
          animation: 'fadeIn 0.3s ease-out',
          '@keyframes fadeIn': {
            '0%': {
              opacity: 0,
              transform: 'translateY(20px)',
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
        }
      }}
    >
      <DialogTitle sx={dialogTitleStyles}>
        <Typography variant="h6" component="div" fontWeight={600} color="text.primary">
          Update Password
        </Typography>
        <IconButton 
          onClick={onClose} 
          sx={closeButtonStyles} 
          aria-label="close"
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      {isSubmitting && (
        <LinearProgress 
          sx={{ 
            borderRadius: 0,
            height: 4 
          }} 
        />
      )}

      <DialogContent dividers sx={dialogContentStyles}>
        <form onSubmit={formik.handleSubmit}>
          <Box component="section" mb={3}>
            <Typography variant="subtitle1" mb={1} fontWeight={500}>
              New Password
            </Typography>
            <TextField
              fullWidth
              id="newPassword"
              type={showNewPassword ? "text" : "password"}
              {...formik.getFieldProps("newPassword")}
              error={Boolean(
                formik.errors.newPassword && formik.touched.newPassword
              )}
              helperText={
                formik.touched.newPassword && formik.errors.newPassword
              }
              slotProps={{
                formHelperText: { sx: helperTextStyles },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showNewPassword ? "Hide password" : "Show password"}>
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        size="small"
                        sx={{
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          }
                        }}
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              aria-label="Enter new password"
              sx={{
                '& .MuiOutlinedInput-root': {
                  transition: 'all 0.2s ease-in-out',
                  '&.Mui-focused': {
                    boxShadow: '0 0 0 2px rgba(0,0,0,0.1)',
                  },
                  borderRadius: 1.5,
                },
              }}
            />

            {formik.values.newPassword && (
              <Fade in={!!formik.values.newPassword}>
                <Box mt={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="body2" fontWeight={500}>
                      Password Strength: {getPasswordStrengthLabel()}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight={600}
                      color={getPasswordStrengthColor()}
                    >
                      {passwordStrength}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={passwordStrength} 
                    sx={{
                      height: 6,
                      borderRadius: 1,
                      backgroundColor: theme.palette.grey[200],
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getPasswordStrengthColor(),
                      }
                    }}
                  />
                  
                  <Box mt={2}>
                    <Typography variant="body2" fontWeight={500} mb={1}>
                      Requirements:
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 1 }}>
                      <RequirementItem 
                        text={`${PASSWORD_REQUIREMENTS.minLength}+ characters`} 
                        satisfied={hasMinLength} 
                      />
                      <RequirementItem 
                        text="At least 1 uppercase letter" 
                        satisfied={hasUppercase} 
                      />
                      <RequirementItem 
                        text="At least 1 number" 
                        satisfied={hasNumber} 
                      />
                      <RequirementItem 
                        text="At least 1 special character" 
                        satisfied={hasSpecial} 
                      />
                    </Box>
                  </Box>
                </Box>
              </Fade>
            )}
          </Box>

          <Box component="section">
            <Typography variant="subtitle1" mb={1} fontWeight={500}>
              Confirm Password
            </Typography>
            <TextField
              fullWidth
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              {...formik.getFieldProps("confirmPassword")}
              error={Boolean(
                formik.errors.confirmPassword && formik.touched.confirmPassword
              )}
              helperText={
                formik.touched.confirmPassword && formik.errors.confirmPassword
              }
              slotProps={{
                formHelperText: { sx: helperTextStyles },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showConfirmPassword ? "Hide password" : "Show password"}>
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                        size="small"
                        sx={{
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          }
                        }}
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              aria-label="Confirm new password"
              sx={{
                '& .MuiOutlinedInput-root': {
                  transition: 'all 0.2s ease-in-out',
                  '&.Mui-focused': {
                    boxShadow: '0 0 0 2px rgba(0,0,0,0.1)',
                  },
                  borderRadius: 1.5,
                },
              }}
            />
            {formik.values.confirmPassword && formik.values.confirmPassword === formik.values.newPassword && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <CheckCircle color="success" fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body2" color="success.main">
                  Passwords match
                </Typography>
              </Box>
            )}
          </Box>
        </form>
      </DialogContent>

      <DialogActions sx={dialogActionsStyles}>
        <WhiteButtonComponent
          onClick={onClose}
          sx={{ 
            marginRight: 2,
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            }
          }}
          aria-label="Cancel"
          btnText="Cancel"
        />

        <BlackButtonComponent
          aria-label="submit"
          disabled={!formik.isValid || !formik.dirty || isSubmitting}
          onClick={formik.handleSubmit}
          btnText={isSubmitting ? "Updating..." : "Update Password"}
          sx={{
            position: 'relative',
            transition: 'all 0.2s',
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

// Helper component for requirement items
const RequirementItem = ({ text, satisfied }: { text: string, satisfied: boolean }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <CheckCircle 
      fontSize="small" 
      color={satisfied ? "success" : "disabled"}
      sx={{ 
        opacity: satisfied ? 1 : 0.5,
        transition: 'all 0.2s ease-in-out',
      }}
    />
    <Typography 
      variant="body2" 
      color={satisfied ? "text.primary" : "text.secondary"}
      sx={{ 
        fontWeight: satisfied ? 500 : 400,
        transition: 'all 0.2s ease-in-out',
      }}
    >
      {text}
    </Typography>
  </Box>
);

const dialogTitleStyles = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  py: 2,
  px: 3,
  fontWeight: 600,
  color: "text.primary",
  borderBottom: "1px solid",
  borderColor: "divider",
};

const closeButtonStyles = {
  color: "text.secondary",
  transition: "all 0.2s ease-in-out",
  "&:hover": { 
    backgroundColor: "action.hover",
    transform: "rotate(90deg)",
  },
};

const dialogContentStyles = {
  px: 3,
  py: 3,
  "& .MuiTextField-root": { mb: 1 },
};

const dialogActionsStyles = {
  px: 3,
  py: 2.5,
  borderTop: 1,
  borderColor: "divider",
};

const helperTextStyles = {
  fontSize: "0.8rem",
  mx: 0,
  mt: 0.5,
};

export default ChangePasswordModal;